import { useState } from 'react';
import { X, ShoppingBag, Trash2 } from 'lucide-react';
import { useCurrency } from '@kss/currency';
import { cn } from '../lib/cn';
import { Button } from '../primitives/Button';
import { VegMark } from '../primitives/VegMark';
import { QtyStepper } from '../forms/QtyStepper';
import { useCart } from './CartProvider';

/**
 * Slide-in cart drawer (right side). Opens via useCart().openCart() — the
 * storefront calls it on "add to cart". Router-agnostic: pass `onCheckout`
 * to wire the primary CTA to a route. GST and coupons are NOT shown here — they
 * apply on the final bill (Pay bill), not at order time.
 */
export function CartDrawer({ onCheckout }: { onCheckout?: () => void }) {
  const { items, count, subTotal, isOpen, closeCart, inc, dec, remove } = useCart();
  const { format } = useCurrency();
  const [confirmId, setConfirmId] = useState<number | null>(null);

  return (
    <div
      className={cn(
        'fixed inset-0 z-[90] transition-opacity duration-200',
        isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
      )}
      aria-hidden={!isOpen}
    >
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/40' onClick={closeCart} />

      {/* Panel */}
      <aside
        role='dialog'
        aria-label='Cart'
        className={cn(
          'absolute right-0 top-0 flex h-full w-full max-w-[380px] flex-col bg-surface-card shadow-pop transition-transform duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className='flex items-center gap-2 border-b border-border px-4 py-3.5'>
          <ShoppingBag size={18} className='text-primary' />
          <span className='font-display text-[15px] font-bold'>
            Your cart{count > 0 && <span className='text-content-muted'> · {count}</span>}
          </span>
          <button
            onClick={closeCart}
            aria-label='Close cart'
            className='ml-auto flex h-8 w-8 items-center justify-center rounded-lg text-content-secondary hover:bg-surface-card-hover'
          >
            <X size={18} />
          </button>
        </div>

        {/* Items */}
        <div className='flex-1 overflow-y-auto px-4 py-3'>
          {items.length === 0 ? (
            <div className='flex h-full flex-col items-center justify-center gap-2 text-center'>
              <ShoppingBag size={32} className='text-content-muted' />
              <p className='text-sm text-content-muted'>Your cart is empty</p>
            </div>
          ) : (
            <div className='flex flex-col gap-3'>
              {items.map((i) => (
                <div key={i.id} className='flex items-center gap-3'>
                  <div
                    className='h-14 w-14 flex-shrink-0 rounded-xl bg-surface-card-hover'
                    style={
                      i.image
                        ? { backgroundImage: `url(${i.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                        : undefined
                    }
                  />
                  <div className='min-w-0 flex-1'>
                    <div className='flex min-w-0 items-center gap-1.5 font-display text-[12.5px] font-bold'>
                      {typeof i.veg === 'boolean' && <VegMark veg={i.veg} bare />}
                      <span className='truncate'>{i.name}</span>
                    </div>
                    <div className='mt-0.5 text-[11px] text-content-muted'>{format(i.price)}</div>
                    {i.note && (
                      <div className='mt-0.5 truncate text-[10px] italic text-content-secondary'>
                        📝 {i.note}
                      </div>
                    )}
                  </div>
                  <div className='flex flex-col items-end gap-1.5'>
                    {confirmId === i.id ? (
                      <div className='flex items-center gap-2'>
                        <span className='text-[10px] font-semibold text-content-secondary'>Remove?</span>
                        <button
                          onClick={() => {
                            remove(i.id);
                            setConfirmId(null);
                          }}
                          className='rounded-md bg-danger px-2 py-0.5 text-[10px] font-bold text-white'
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className='text-[10px] font-bold text-content-muted'
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <>
                        <QtyStepper
                          qty={i.qty}
                          onInc={() => inc(i.id)}
                          onDec={() => (i.qty === 1 ? setConfirmId(i.id) : dec(i.id))}
                        />
                        <button
                          onClick={() => setConfirmId(i.id)}
                          aria-label={`Remove ${i.name}`}
                          className='flex items-center gap-1 text-[10px] font-semibold text-content-muted hover:text-danger'
                        >
                          <Trash2 size={11} /> Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Charges + CTA */}
        {items.length > 0 && (
          <div className='border-t border-border px-4 py-3.5'>
            <Row label='Item total' value={format(subTotal)} />
            <div className='mt-2 flex items-center justify-between border-t border-border pt-2'>
              <span className='font-display text-[13px] font-bold'>To pay</span>
              <span className='font-display text-base font-extrabold text-primary'>{format(subTotal)}</span>
            </div>
            <p className='mt-1 text-right text-[9.5px] text-content-muted'>Taxes &amp; offers applied on the final bill</p>
            <Button block size='lg' className='mt-3' onClick={onCheckout ?? closeCart}>
              Checkout →
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      className={cn(
        'flex justify-between py-0.5 text-[11.5px]',
        accent ? 'font-bold text-veg' : 'text-content-secondary'
      )}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
